import type { UseFormSetError, UseFormSetValue, FieldPath, FieldValues } from 'react-hook-form';

/**
 * Validates and handles numeric input for wound measurement fields (length, width, depth).
 * Allows decimals, intermediate typing states, and enforces minimum value > 0.
 * 
 * @param inputValue - The raw input value from the user
 * @param fieldName - The field name to update
 * @param setValue - React Hook Form's setValue function
 * @param setError - React Hook Form's setError function
 */
export function handleNumericInputChange<TFieldValues extends FieldValues>(
  inputValue: string,
  fieldName: FieldPath<TFieldValues>,
  setValue: UseFormSetValue<TFieldValues>,
  setError: UseFormSetError<TFieldValues>,
): void {

  const numValue = Number(inputValue);
  if (isNaN(numValue)) {
    return;
  }

  // Allow empty string (user can backspace all)
  if (inputValue === '') {
    setValue(fieldName, null as any);
    setError(fieldName, { type: 'manual', message: '' });
    return;
  }
  
  // Check if input contains alphabets - block them
  if (/[a-zA-Z]/.test(inputValue)) {
    setError(fieldName, { 
      type: 'manual', 
      message: 'Only numbers and decimal point are allowed' 
    });
    return;
  }

  // Allow intermediate states where decimal point is at the end (e.g., "1.", "2.", "10.")
  // This allows users to type "1." and continue to "1.1", "1.2", etc.
  if (inputValue.endsWith('.') && inputValue.split('.').length === 2) {
    const integerPart = inputValue.split('.')[0];
    // Only allow if there's at least one digit before the decimal point
    if (integerPart && /^\d+$/.test(integerPart)) {
      const numValue = Number(integerPart);
      // Allow if the integer part is > 0 (e.g., "1.", "2.", "10.")
      if (numValue > 0) {
        setValue(fieldName, inputValue as any);
        setError(fieldName, { type: 'manual', message: '' });
        return;
      }
    }
  }

  // Allow intermediate states like "1.0", "1.00", "2.0", etc. (so user can type "1.01", "2.01", etc.)
  // Check if it has a decimal point and trailing zeros after a non-zero integer part
  if (inputValue.includes('.') && inputValue.split('.').length === 2) {
    const [integerPart, decimalPart] = inputValue.split('.');
    // Check if integer part is > 0 and decimal part contains only zeros
    if (integerPart && /^\d+$/.test(integerPart) && Number(integerPart) > 0) {
      if (decimalPart && /^0+$/.test(decimalPart)) {
        // Allow intermediate states like "1.0", "1.00", "2.0", etc.
        setValue(fieldName, inputValue as any);
        setError(fieldName, { type: 'manual', message: '' });
        return;
      }
    }
  }

  // Allow "0" to be typed (so user can type "0.1" or "0.01"), but show error
  if (inputValue === '0' || inputValue === '0.' || inputValue === '0.0' || inputValue === '0.00') {
    setValue(fieldName, inputValue as any);
    setError(fieldName, { 
      type: 'manual', 
      message: 'Minimum value should be greater than 0' 
    });
    return;
  }
  
  if (inputValue === '0.000') {
    return;
  }
  // Allow intermediate states like "0.0", "0.00" (so user can type "0.01")
  // Check if it starts with "0." and has trailing zeros
  if (inputValue.startsWith('0.') && inputValue.split('.').length === 2) {
    const decimalPart = inputValue.split('.')[1];
    // If all digits after decimal are zeros, it's still intermediate (like "0.0", "0.00")
    // Allow it so user can continue typing to "0.01"
    if (decimalPart && /^0+$/.test(decimalPart)) {
      setValue(fieldName, inputValue as any);
      setError(fieldName, { type: 'manual', message: '' });
      return;
    }
  }
  
  // For complete numbers, validate that value > 0
  if (numValue <= 0) {
    setError(fieldName, { 
      type: 'manual', 
      message: 'Value must be greater than 0' 
    });
    return;
  }
  
  // Valid number > 0 - format and set value
  const formattedValue = numValue % 1 === 0 
    ? numValue 
    : parseFloat(numValue.toFixed(2));
  setValue(fieldName, formattedValue as any);
  setError(fieldName, { type: 'manual', message: '' });
}

